import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { logError } from '../lib/errorLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOG_DEFAULTS = {
  acneLocations: { jawline: false, forehead: false, cheeks: false, chin: false, scalp: false, nose: false },
  acneSeverity: [0],
  bloatingLevel: [0],
  cycleDay: null,
  isPeriod: false,
  foodIntake: [],
  waterGlasses: [0],
  stressLevel: [0],
  sleepHours: [7],
};

/**
 * Custom hook encapsulating all dashboard data fetching.
 * Fixes: hook dependency warnings, empty catch blocks, reduces Dashboard complexity.
 */
export function useDashboardData() {
  const [detoxScore, setDetoxScore] = useState(null);
  const [patterns, setPatterns] = useState(null);
  const [recommendations, setRecommendations] = useState(null);
  const [selfies, setSelfies] = useState([]);
  const [logHistory, setLogHistory] = useState([]);
  const [todayLog, setTodayLog] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  const fetchDetoxScore = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/detox-score/today`, { withCredentials: true });
      setDetoxScore(res.data);
    } catch (err) {
      setFetchError((prev) => ({ ...prev, detoxScore: err.message }));
      logError('useDashboardData', 'Failed to fetch detox score', { error: err.message });
    }
  }, []);

  const fetchPatterns = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/patterns/analyze`, { withCredentials: true });
      setPatterns(res.data);
    } catch (err) {
      setFetchError((prev) => ({ ...prev, patterns: err.message }));
    }
  }, []);

  const fetchRecommendations = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/recommendations`, { withCredentials: true });
      setRecommendations(res.data);
    } catch (err) {
      setFetchError((prev) => ({ ...prev, recommendations: err.message }));
    }
  }, []);

  const fetchSelfies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/selfies`, { withCredentials: true });
      setSelfies(res.data);
    } catch (err) {
      setFetchError((prev) => ({ ...prev, selfies: err.message }));
    }
  }, []);

  const fetchLogHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/logs/history?days=14`, { withCredentials: true });
      setLogHistory(res.data);
    } catch (err) {
      setFetchError((prev) => ({ ...prev, logHistory: err.message }));
    }
  }, []);

  const fetchTodayLog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/logs/today`, { withCredentials: true });
      setTodayLog(res.data);
    } catch (err) {
      setFetchError((prev) => ({ ...prev, todayLog: err.message }));
    }
  }, []);

  const saveLog = useCallback(async (logPayload) => {
    const res = await axios.post(`${API}/logs`, logPayload, { withCredentials: true });
    // Refresh dependent data after saving
    fetchTodayLog();
    fetchDetoxScore();
    fetchPatterns();
    fetchRecommendations();
    fetchLogHistory();
    return res.data;
  }, [fetchTodayLog, fetchDetoxScore, fetchPatterns, fetchRecommendations, fetchLogHistory]);

  const uploadSelfie = useCallback(async (base64) => {
    const res = await axios.post(`${API}/selfies`, { image_base64: base64 }, { withCredentials: true });
    fetchSelfies();
    return res.data;
  }, [fetchSelfies]);

  // Initial data load
  useEffect(() => {
    fetchTodayLog();
    fetchDetoxScore();
    fetchPatterns();
    fetchRecommendations();
    fetchSelfies();
    fetchLogHistory();
  }, [fetchTodayLog, fetchDetoxScore, fetchPatterns, fetchRecommendations, fetchSelfies, fetchLogHistory]);

  return {
    detoxScore, patterns, recommendations, selfies, logHistory, todayLog,
    fetchError, saveLog, uploadSelfie, fetchSelfies,
    LOG_DEFAULTS,
  };
}
